import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import { PbInstrumentInfo } from '@/connect/gen/common/protos/types_pb';
import { Data } from '@/connect';
import { useSocketStore } from '@/store/socket-store';

export const useSecuritiesStore = defineStore('securities', () => {
  const securities = shallowRef(new Map<string, Data<PbInstrumentInfo>>());
  const priceSubscriptions = shallowRef(new Set<string>());
  const socketStore = useSocketStore();
  socketStore.useEventHandler('refreshInstrumentPrice', updateLastPrice);

  function addSecurities(items: Record<string, Data<PbInstrumentInfo>>) {
    securities.value = new Map([...securities.value, ...Object.entries(items)]);
  }

  function getSecurity(cusip: string) {
    return securities.value.get(cusip);
  }

  // request price updates for securities on the current page only
  async function subscribeToSocketPriceUpdates(items: Record<string, Data<PbInstrumentInfo>>) {
    const newCusips = Object.keys(items).filter((cusip) => !priceSubscriptions.value.has(cusip));
    if (newCusips.length === 0) return;
    await socketStore.send({
      action: 'subscribeToPriceUpdates',
      payload: JSON.stringify(newCusips),
    });
    priceSubscriptions.value = new Set([...priceSubscriptions.value, ...newCusips]);
  }

  async function unsubscribeFromSocketPriceUpdates() {
    await socketStore.send({ action: 'unsubscribeFromPriceUpdates', payload: '' });
    priceSubscriptions.value = new Set();
  }

  function updateLastPrice(payload: { price: string; cusip: string }) {
    const instrumentInfo = getSecurity(payload.cusip);
    if (instrumentInfo) {
      const updated = { ...instrumentInfo, lastPrice: payload.price };
      securities.value = new Map(securities.value).set(payload.cusip, updated);
    }
  }

  return {
    addSecurities,
    getSecurity,
    securities,
    subscribeToSocketPriceUpdates,
    unsubscribeFromSocketPriceUpdates,
  };
});

export type SecuritiesStore = ReturnType<typeof useSecuritiesStore>;
